import {
  TREE_RUNTIME_MISSING_PARENT_IDENTITY_BROWSER_MILESTONE,
  type TreeRuntimeMissingParentIdentityBrowserObservation,
} from './TreeRuntimeMissingParentIdentityParityBrowserRuntime';

import {
  TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE,
} from './TreeRuntimeMissingParentIdentityParityAuthority';

export const TREE_RUNTIME_MISSING_PARENT_IDENTITY_PUBLICATION_MILESTONE =
  'CC-TREE-LIVE-003G2S' as const;

export const TREE_RUNTIME_MISSING_PARENT_IDENTITY_PUBLICATION_SOURCE =
  'TreeRuntimeMissingParentIdentityParityEvidence' as const;

export type TreeRuntimeMissingParentIdentityPublicationSharedSeverity =
  'info' | 'warn' | 'fail';

export type TreeRuntimeMissingParentIdentityPublicationRuntimeSeverity =
  'info' | 'warning' | 'error';

export interface TreeRuntimeMissingParentIdentityPublicationProjection {
  readonly milestone:
    typeof TREE_RUNTIME_MISSING_PARENT_IDENTITY_PUBLICATION_MILESTONE;

  readonly source:
    typeof TREE_RUNTIME_MISSING_PARENT_IDENTITY_PUBLICATION_SOURCE;

  readonly event: string;

  readonly sharedSeverity:
    TreeRuntimeMissingParentIdentityPublicationSharedSeverity;

  readonly runtimeSeverity:
    TreeRuntimeMissingParentIdentityPublicationRuntimeSeverity;

  readonly summary: string;

  readonly evidence:
    readonly string[];

  readonly metadata:
    Readonly<Record<string, unknown>>;

  readonly browserStatus:
    TreeRuntimeMissingParentIdentityBrowserObservation['status'];

  readonly identityParityProven: boolean;

  readonly repairPlanningEligible: boolean;

  readonly repairReady: false;

  readonly identitySuppressed: true;

  readonly readOnly: true;

  readonly repairExecuted: false;
}

const validCount = (
  value: unknown,
): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= 0 &&
  value <= 1_000_000;

export function projectTreeRuntimeMissingParentIdentityParityPublication(
  observation:
    TreeRuntimeMissingParentIdentityBrowserObservation,
): TreeRuntimeMissingParentIdentityPublicationProjection | null {
  if (
    observation.milestone !==
      TREE_RUNTIME_MISSING_PARENT_IDENTITY_BROWSER_MILESTONE ||
    observation.identitySuppressed !== true ||
    observation.readOnly !== true ||
    observation.repairExecuted !== false ||
    observation.repairReady !== false
  ) {
    return null;
  }

  if (
    observation.browserMissingReferencePairsCount !== null &&
    !validCount(
      observation.browserMissingReferencePairsCount,
    )
  ) {
    return null;
  }

  const decision =
    observation.decision;

  if (observation.status !== 'DECIDED') {
    if (
      decision !== null ||
      observation.identityParityProven !== false ||
      observation.repairPlanningEligible !== false ||
      observation.repairReadinessStatus !== null
    ) {
      return null;
    }

    const event =
      `TREE_MISSING_PARENT_IDENTITY_PARITY_${observation.status}`;

    return Object.freeze({
      milestone:
        TREE_RUNTIME_MISSING_PARENT_IDENTITY_PUBLICATION_MILESTONE,
      source:
        TREE_RUNTIME_MISSING_PARENT_IDENTITY_PUBLICATION_SOURCE,
      event,
      sharedSeverity: 'warn',
      runtimeSeverity: 'warning',
      summary:
        `Tree missing-parent identity parity browser observation is ${observation.status}.`,
      evidence: Object.freeze([
        `browserStatus=${observation.status}`,
        `browserMissingReferencePairsCount=${observation.browserMissingReferencePairsCount ?? 'unknown'}`,
        'identityParityProven=false',
        'repairPlanningEligible=false',
        'repairReady=false',
        'identitySuppressed=true',
      ]),
      metadata: Object.freeze({
        browserStatus:
          observation.status,
        browserMissingReferencePairsCount:
          observation.browserMissingReferencePairsCount,
        identityParityProven: false,
        repairPlanningEligible: false,
        repairReadinessStatus: null,
        decisionStatus: null,
        serverMissingReferencePairsCount: null,
        identitySuppressed: true,
        readOnly: true,
        repairExecuted: false,
        repairReady: false,
      }),
      browserStatus:
        observation.status,
      identityParityProven: false,
      repairPlanningEligible: false,
      repairReady: false,
      identitySuppressed: true,
      readOnly: true,
      repairExecuted: false,
    });
  }

  if (
    decision === null ||
    decision.milestone !==
      TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE ||
    decision.readOnly !== true ||
    decision.repairExecuted !== false ||
    decision.repairReady !== false ||
    observation.browserMissingReferencePairsCount === null ||
    observation.browserMissingReferencePairsCount !==
      decision.browserMissingReferencePairsCount ||
    observation.identityParityProven !==
      decision.identityParityProven ||
    observation.repairPlanningEligible !==
      decision.repairPlanningEligible ||
    observation.repairReadinessStatus !==
      decision.repairReadinessStatus ||
    (
      decision.serverMissingReferencePairsCount !== null &&
      !validCount(
        decision.serverMissingReferencePairsCount,
      )
    )
  ) {
    return null;
  }

  const lifecycle =
    decision.missingParentReferenceLifecycle;

  const event =
    decision.identityParityProven
      ? 'TREE_MISSING_PARENT_IDENTITY_PARITY_PROVEN'
      : 'TREE_MISSING_PARENT_IDENTITY_PARITY_BLOCKED';

  const sharedSeverity =
    decision.identityParityProven
      ? decision.repairPlanningEligible
        ? 'warn'
        : 'info'
      : 'fail';

  const runtimeSeverity =
    sharedSeverity === 'fail'
      ? 'error'
      : sharedSeverity === 'warn'
        ? 'warning'
        : 'info';

  return Object.freeze({
    milestone:
      TREE_RUNTIME_MISSING_PARENT_IDENTITY_PUBLICATION_MILESTONE,
    source:
      TREE_RUNTIME_MISSING_PARENT_IDENTITY_PUBLICATION_SOURCE,
    event,
    sharedSeverity,
    runtimeSeverity,
    summary:
      decision.identityParityProven
        ? `Tree missing-parent identity parity proven for ${decision.browserMissingReferencePairsCount} browser reference pair(s).`
        : `Tree missing-parent identity parity blocked with decision ${decision.status}.`,
    evidence: Object.freeze([
      `browserStatus=${observation.status}`,
      `decisionStatus=${decision.status}`,
      `browserMissingReferencePairsCount=${decision.browserMissingReferencePairsCount}`,
      `serverMissingReferencePairsCount=${decision.serverMissingReferencePairsCount ?? 'unknown'}`,
      `identityParityProven=${decision.identityParityProven}`,
      `repairPlanningEligible=${decision.repairPlanningEligible}`,
      `repairReadinessStatus=${decision.repairReadinessStatus}`,
      `repairReady=false`,
      'identitySuppressed=true',
      `missingParentReferenceLifecycle=${lifecycle ? 'present' : 'absent'}`,
      `missingParentReferenceActiveSameGraphCount=${lifecycle?.missingParentReferenceActiveSameGraphCount ?? 'unknown'}`,
      `missingParentReferenceSoftDeletedSameGraphCount=${lifecycle?.missingParentReferenceSoftDeletedSameGraphCount ?? 'unknown'}`,
      `missingParentReferenceCrossGraphCount=${lifecycle?.missingParentReferenceCrossGraphCount ?? 'unknown'}`,
      `missingParentReferencePhysicallyAbsentCount=${lifecycle?.missingParentReferencePhysicallyAbsentCount ?? 'unknown'}`,
      `missingParentReferenceIndeterminateCount=${lifecycle?.missingParentReferenceIndeterminateCount ?? 'unknown'}`,
    ]),
    metadata: Object.freeze({
      browserStatus:
        observation.status,
      browserMissingReferencePairsCount:
        decision.browserMissingReferencePairsCount,
      serverMissingReferencePairsCount:
        decision.serverMissingReferencePairsCount,
      decisionStatus:
        decision.status,
      identityParityProven:
        decision.identityParityProven,
      repairPlanningEligible:
        decision.repairPlanningEligible,
      repairReadinessStatus:
        decision.repairReadinessStatus,
      missingParentReferenceLifecycle:
        lifecycle
          ? Object.freeze({
              missingParentReferenceActiveSameGraphCount:
                lifecycle.missingParentReferenceActiveSameGraphCount,
              missingParentReferenceSoftDeletedSameGraphCount:
                lifecycle.missingParentReferenceSoftDeletedSameGraphCount,
              missingParentReferenceCrossGraphCount:
                lifecycle.missingParentReferenceCrossGraphCount,
              missingParentReferencePhysicallyAbsentCount:
                lifecycle.missingParentReferencePhysicallyAbsentCount,
              missingParentReferenceIndeterminateCount:
                lifecycle.missingParentReferenceIndeterminateCount,
            })
          : null,
      identitySuppressed: true,
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
    }),
    browserStatus:
      observation.status,
    identityParityProven:
      decision.identityParityProven,
    repairPlanningEligible:
      decision.repairPlanningEligible,
    repairReady: false,
    identitySuppressed: true,
    readOnly: true,
    repairExecuted: false,
  });
}

export function createTreeRuntimeMissingParentIdentityParityPublicationFingerprint(
  observation:
    TreeRuntimeMissingParentIdentityBrowserObservation,
): string {
  const projection =
    projectTreeRuntimeMissingParentIdentityParityPublication(
      observation,
    );

  if (!projection) {
    return 'INVALID_PUBLICATION';
  }

  return JSON.stringify({
    milestone:
      projection.milestone,
    browserStatus:
      projection.browserStatus,
    identityParityProven:
      projection.identityParityProven,
    repairPlanningEligible:
      projection.repairPlanningEligible,
    evidence:
      projection.evidence,
  });
}
